import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import {
  getProfile,
  getCurrentDoctor,
  createOrUpdateProfile,
  updateProfilePartial,
  updateOnboardingStep,
  getOnboardingSummary,
  getProfileCompletion,
  getOnboardingStatus,
  getCountryCodes,
  getTimezones,
  getGenderTypes,
  getSpecializations,
  getSubspecializations,
  getCouncilInfo,
  getAllMedicalCouncils,
  getDegreeTypes,
  getPracticeTypes,
  getCertificationTypes,
  getEducation,
  getCertifications,
  getDocuments,
  getPractices,
  getServices,
  getBankDetails,
  submitOnboarding,
  addEducation,
  deleteEducation,
  addCertification,
  deleteCertification,
  uploadDocument,
  updateDocument,
  deleteDocument,
  addPractice,
  deletePractice,
  addPracticeTiming,
  addService,
  deleteService,
  createOrUpdateBankDetails,
  getServiceCategories,
  getMedicalServicesDictionary,
} from '../../services/doctor-onboarding/doctorOnboardingService';
import type {
  DoctorProfile,
  OnboardingStep,
  CountryCode,
  Timezone,
  GenderType,
  Specialization,
  Subspecialization,
  MedicalCouncil,
  CouncilInfo,
  DoctorEducation,
  DoctorCertification,
  DoctorDocument,
  DoctorPractice,
  DoctorPracticeTiming,
  DoctorService,
  DoctorBankDetails,
  DoctorBankDetailsInput,
  ServiceCategory,
  MedicalService,
} from '../../types/doctorOnboarding';
import { ONBOARDING_STEP_LABELS } from '../../types/doctorOnboarding';
import PersonalInfoStep from './steps/PersonalInfoStep';
import ProfessionalInfoStep from './steps/ProfessionalInfoStep';
import EducationStep from './steps/EducationStep';
import CertificationsStep from './steps/CertificationsStep';
import DocumentsStep from './steps/DocumentsStep';
import PracticesStep from './steps/PracticesStep';
import ServicesStep from './steps/ServicesStep';
import BankDetailsStep from './steps/BankDetailsStep';
import BioStep from './steps/BioStep';
import ReviewSubmitStep from './steps/ReviewSubmitStep';
import './DoctorOnboardingPage.css';

const STEP_ORDER: OnboardingStep[] = [
  'personalInfo',
  'professionalInfo',
  'education',
  'certifications',
  'documents',
  'practices',
  'services',
  'bankDetails',
  'bio',
  'review',
];

export default function DoctorOnboardingPage() {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<DoctorProfile | null>(null);
  const [doctorId, setDoctorId] = useState<number | null>(null);

  // Dictionaries (same as mobile)
  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);
  const [timezones, setTimezones] = useState<Timezone[]>([]);
  const [genderTypes, setGenderTypes] = useState<GenderType[]>([]);
  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [subspecializations, setSubspecializations] = useState<Subspecialization[]>([]);
  const [councilInfo, setCouncilInfo] = useState<CouncilInfo | null>(null);
  const [allMedicalCouncils, setAllMedicalCouncils] = useState<MedicalCouncil[]>([]);
  const [degreeTypes, setDegreeTypes] = useState<any[]>([]);
  const [practiceTypes, setPracticeTypes] = useState<any[]>([]);
  const [certificationTypes, setCertificationTypes] = useState<any[]>([]);
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([]);
  const [medicalServices, setMedicalServices] = useState<MedicalService[]>([]);
  const [educationEntries, setEducationEntries] = useState<DoctorEducation[]>([]);
  const [certifications, setCertifications] = useState<DoctorCertification[]>([]);
  const [documents, setDocuments] = useState<DoctorDocument[]>([]);
  const [practices, setPractices] = useState<DoctorPractice[]>([]);
  const [services, setServices] = useState<DoctorService[]>([]);
  const [bankDetails, setBankDetails] = useState<DoctorBankDetails | null>(null);
  /** Session-only: full account number for pre-fill when user goes back (API returns masked only) */
  const [lastSavedAccountNumber, setLastSavedAccountNumber] = useState<string | null>(null);
  const [canSubmit, setCanSubmit] = useState(false);
  const [missingRequirements, setMissingRequirements] = useState<string[]>([]);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [onboardingStatus, setOnboardingStatus] = useState<string | null>(null);
  const [verificationNotes, setVerificationNotes] = useState<string | null>(null);
  const [nextAction, setNextAction] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const currentStep = STEP_ORDER[currentStepIndex];

  const formatError = (error: any, fallback: string) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => (typeof item?.msg === 'string' ? item.msg : JSON.stringify(item)))
        .filter(Boolean);
      if (messages.length) return messages.join(', ');
    }
    if (detail && typeof detail === 'object') {
      if (typeof detail.msg === 'string') return detail.msg;
      return JSON.stringify(detail);
    }
    return error?.message || fallback;
  };

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [doc, prof] = await Promise.all([
        getCurrentDoctor(),
        getProfile().catch(() => null),
      ]);
      setDoctorId(doc.id);
      setProfile(prof || null);
      const stepValue =
        (prof as any)?.current_onboarding_step ?? (prof as any)?.onboarding_step;
      if (stepValue != null && stepValue >= 1) {
        const idx = Math.min(stepValue - 1, STEP_ORDER.length - 1);
        setCurrentStepIndex(idx);
      }
      await Promise.all([
        getCountryCodes().then(setCountryCodes),
        getTimezones().then(setTimezones),
        getGenderTypes().then(setGenderTypes),
        getSpecializations().then(setSpecializations),
        getAllMedicalCouncils().then(setAllMedicalCouncils),
        getDegreeTypes().then(setDegreeTypes),
        getPracticeTypes().then(setPracticeTypes),
        getCertificationTypes().then(setCertificationTypes),
        getServiceCategories().then(setServiceCategories),
        getMedicalServicesDictionary().then(setMedicalServices),
      ]);
      const completion = await getProfileCompletion().catch(() => null);
      if (completion) {
        setCanSubmit(!!completion.can_submit);
        setMissingRequirements(completion.missing_fields || []);
        setCompletionPercentage(completion.completion_percentage ?? 0);
      }
      const status = await getOnboardingStatus().catch(() => null);
      if (status) {
        setOnboardingStatus(status.onboarding_status ?? null);
        setVerificationNotes((status as any).verification_notes ?? null);
        setNextAction((status as any).next_action ?? null);
        if ((status.onboarding_status || '').toLowerCase() === 'approved') {
          navigate('/dashboard');
          return;
        }
      }
    } catch (e: any) {
      setError(formatError(e, 'Failed to load'));
      if (e?.response?.status === 401) {
        navigate('/login');
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadStepData = useCallback(async () => {
    if (!profile) return;
    try {
      if (currentStepIndex >= 2) setEducationEntries(await getEducation());
      if (currentStepIndex >= 3) setCertifications(await getCertifications());
      if (currentStepIndex >= 4) setDocuments(await getDocuments());
      if (currentStepIndex >= 5) setPractices(await getPractices());
      if (currentStepIndex >= 6) setServices(await getServices());
      if (currentStepIndex >= 7) setBankDetails(await getBankDetails());
      const summary = await getOnboardingSummary().catch(() => null);
      if (summary) {
        setCanSubmit(!!summary.can_submit);
        setMissingRequirements(summary.missing_requirements || []);
        setCompletionPercentage(summary.completion_percentage ?? 0);
      }
      const status = await getOnboardingStatus().catch(() => null);
      if (status) {
        setOnboardingStatus(status.onboarding_status ?? null);
        setVerificationNotes((status as any).verification_notes ?? null);
        setNextAction((status as any).next_action ?? null);
        if ((status.onboarding_status || '').toLowerCase() === 'approved') {
          navigate('/dashboard');
          return;
        }
      }
    } catch {
      // ignore per-step load errors
    }
  }, [profile, currentStepIndex, navigate]);

  useEffect(() => {
    loadStepData();
  }, [loadStepData]);

  const loadSubspecializations = useCallback(async (specializationId: number) => {
    const subs = await getSubspecializations(specializationId);
    setSubspecializations(subs);
  }, []);

  const loadCouncilInfoForSpec = useCallback(async (specializationId: number) => {
    const info = await getCouncilInfo(specializationId);
    setCouncilInfo(info);
  }, []);

  useEffect(() => {
    if (profile?.specialization_id) {
      loadCouncilInfoForSpec(profile.specialization_id);
    } else {
      setCouncilInfo(null);
    }
  }, [profile?.specialization_id, loadCouncilInfoForSpec]);

  const handleSavePersonal = async (personal: Partial<DoctorProfile>) => {
    setSaving(true);
    setError(null);
    try {
      if (!doctorId) throw new Error('Doctor ID missing');
      if (!profile) {
        const created = await createOrUpdateProfile({
          doctor_id: doctorId,
          title: personal.title ?? undefined,
          first_name: personal.first_name!,
          last_name: personal.last_name!,
          date_of_birth: personal.date_of_birth!,
          gender: personal.gender!,
          contact_number: personal.contact_number!,
          country_code_id: personal.country_code_id ?? undefined,
          timezone_id: personal.timezone_id ?? undefined,
          middle_name: personal.middle_name ?? undefined,
          address_line1: personal.address_line1 ?? undefined,
          address_line2: personal.address_line2 ?? undefined,
          city: personal.city ?? undefined,
          state: personal.state ?? undefined,
          country: personal.country ?? undefined,
          pincode: personal.pincode ?? undefined,
          years_experience: 0,
        } as DoctorProfile);
        setProfile(created);
      } else {
        const updated = await updateProfilePartial({
          title: personal.title,
          first_name: personal.first_name,
          last_name: personal.last_name,
          date_of_birth: personal.date_of_birth,
          gender: personal.gender,
          contact_number: personal.contact_number,
          country_code_id: personal.country_code_id,
          timezone_id: personal.timezone_id,
          middle_name: personal.middle_name,
          address_line1: personal.address_line1,
          address_line2: personal.address_line2,
          city: personal.city,
          state: personal.state,
          country: personal.country,
          pincode: personal.pincode,
        });
        setProfile(updated);
      }
      await updateOnboardingStep(2);
      setCurrentStepIndex(1);
    } catch (e: any) {
      setError(formatError(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfessional = async (professional: Partial<DoctorProfile>) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateProfilePartial({
        specialization_id: professional.specialization_id,
        subspecialization_id: professional.subspecialization_id,
        years_experience: professional.years_experience,
        registration_number: professional.registration_number,
        registration_council: professional.registration_council,
        registration_year: professional.registration_year,
        languages_spoken: professional.languages_spoken,
      });
      setProfile(updated);
      await updateOnboardingStep(3);
      setCurrentStepIndex(2);
    } catch (e: any) {
      setError(formatError(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddEducation = async (body: Omit<DoctorEducation, 'id'>) => {
    const created = await addEducation(body);
    setEducationEntries((prev) => [...prev, created]);
  };

  const handleDeleteEducation = async (id: number) => {
    await deleteEducation(id);
    setEducationEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const handleAddCertification = async (body: Omit<DoctorCertification, 'id'>) => {
    const created = await addCertification(body);
    setCertifications((prev) => [...prev, created]);
  };

  const handleDeleteCertification = async (id: number) => {
    await deleteCertification(id);
    setCertifications((prev) => prev.filter((c) => c.id !== id));
  };

  const handleUploadDocument = async (
    file: File,
    documentType: string,
    documentSubType?: string,
    documentNumber?: string
  ) => {
    const uploaded = await uploadDocument(file, documentType);
    if (documentSubType || documentNumber) {
      const updated = await updateDocument(uploaded.id, {
        document_subtype: documentSubType,
        document_number: documentNumber,
      });
      setDocuments((prev) => [...prev, updated]);
      return;
    }
    setDocuments((prev) => [...prev, uploaded]);
  };

  const handleDeleteDocument = async (id: number) => {
    await deleteDocument(id);
    setDocuments((prev) => prev.filter((d) => d.id !== id));
  };

  const handleAddPractice = async (
    body: Omit<DoctorPractice, 'id' | 'created_at' | 'updated_at'>
  ): Promise<DoctorPractice> => {
    const created = await addPractice(body);
    setPractices((prev) => [...prev, created]);
    return created;
  };

  const handleDeletePractice = async (id: number) => {
    await deletePractice(id);
    setPractices((prev) => prev.filter((p) => p.id !== id));
  };

  const handleAddPracticeTiming = async (
    practiceId: number,
    timing: Omit<DoctorPracticeTiming, 'id' | 'practice_id' | 'created_at' | 'updated_at'>
  ) => {
    const created = await addPracticeTiming(practiceId, timing);
    setPractices((prev) =>
      prev.map((p) =>
        p.id === practiceId
          ? { ...p, timings: [...(p.timings || []), created] }
          : p
      )
    );
  };

  const handleAddService = async (
    body: Omit<DoctorService, 'id' | 'created_at' | 'updated_at'>
  ) => {
    const created = await addService(body);
    setServices((prev) => [...prev, created]);
  };

  const handleDeleteService = async (id: number) => {
    await deleteService(id);
    setServices((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSaveBankDetails = async (
    body: DoctorBankDetailsInput
  ) => {
    const saved = await createOrUpdateBankDetails(body);
    setBankDetails(saved);
    setLastSavedAccountNumber(body.account_number?.trim() ?? null);
  };

  const handleSaveBio = async (bio: string, profilePhotoUrl?: string) => {
    const updated = await updateProfilePartial({
      bio,
      profile_photo_url: profilePhotoUrl,
    });
    setProfile(updated);
  };

  const goBack = () => {
    setError(null);
    if (currentStepIndex > 0) setCurrentStepIndex(currentStepIndex - 1);
  };

  const goToStepIndex = async (index: number) => {
    setError(null);
    setCurrentStepIndex(index);
    try {
      await updateOnboardingStep(index + 1);
    } catch {
      // non-blocking
    }
  };

  const handleSubmitForVerification = async () => {
    setSaving(true);
    setError(null);
    try {
      await submitOnboarding();
      setSubmitted(true);
      const status = await getOnboardingStatus().catch(() => null);
      if (status) {
        setOnboardingStatus(status.onboarding_status ?? null);
        setVerificationNotes((status as any).verification_notes ?? null);
        setNextAction((status as any).next_action ?? null);
      }
    } catch (e: any) {
      setError(formatError(e, 'Submit failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleCancel = () => {
    authService.clearTokens();
    navigate('/login');
  };

  if (submitted) {
    return (
      <div className="doctor-onboarding-page">
        <header className="onboarding-header">
          <h1>Submission Complete</h1>
          <p className="onboarding-step-label">
            Your profile has been submitted for verification. You can sign in after admin approval.
          </p>
        </header>
        {onboardingStatus && (
          <div className="onboarding-section">
            <h3>Status</h3>
            <p className="onboarding-step-label">Onboarding: {onboardingStatus}</p>
            {verificationNotes && (
              <div className="onboarding-error">Notes: {verificationNotes}</div>
            )}
            {nextAction && (
              <p className="onboarding-step-label">Next: {nextAction}</p>
            )}
          </div>
        )}
        <div className="step-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/doctor-review-status')}>
            View Review Status
          </button>
          <button type="button" className="btn-primary" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="doctor-onboarding-page">
        <div className="onboarding-loading">Loading your profile...</div>
      </div>
    );
  }

  return (
    <div className="doctor-onboarding-page">
      <header className="onboarding-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1>Doctor Onboarding</h1>
          <button type="button" className="btn-secondary" onClick={handleCancel}>
            Cancel
          </button>
        </div>
        <div className="onboarding-progress">
          <div
            className="onboarding-progress-bar"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
        <p className="onboarding-step-label">
          Step {currentStepIndex + 1} of {STEP_ORDER.length}: {ONBOARDING_STEP_LABELS[currentStep]}
        </p>
      </header>

      {error && (
        <div className="onboarding-error" role="alert">
          {error}
        </div>
      )}

      <div className="onboarding-content">
        {currentStep === 'personalInfo' && (
          <PersonalInfoStep
            profile={profile}
            countryCodes={countryCodes}
            timezones={timezones}
            genderTypes={genderTypes}
            onSave={handleSavePersonal}
            saving={saving}
            onBack={goBack}
          />
        )}
        {currentStep === 'professionalInfo' && (
          <ProfessionalInfoStep
            profile={profile}
            specializations={specializations}
            subspecializations={subspecializations}
            councilInfo={councilInfo}
            allMedicalCouncils={allMedicalCouncils}
            onLoadSubspecializations={loadSubspecializations}
            onLoadCouncilInfo={loadCouncilInfoForSpec}
            onSave={handleSaveProfessional}
            saving={saving}
            onBack={goBack}
          />
        )}
        {currentStep === 'education' && (
          <EducationStep
            doctorId={doctorId}
            entries={educationEntries}
            degreeTypes={degreeTypes}
            specializations={specializations}
            subspecializations={subspecializations}
            onLoadSubspecializations={loadSubspecializations}
            onAdd={handleAddEducation}
            onDelete={handleDeleteEducation}
            onNext={() => goToStepIndex(3)}
            onBack={goBack}
          />
        )}
        {currentStep === 'certifications' && (
          <CertificationsStep
            doctorId={doctorId}
            entries={certifications}
            certificationTypes={certificationTypes}
            specializations={specializations}
            subspecializations={subspecializations}
            onLoadSubspecializations={loadSubspecializations}
            onAdd={handleAddCertification}
            onDelete={handleDeleteCertification}
            onNext={() => goToStepIndex(4)}
            onBack={goBack}
          />
        )}
        {currentStep === 'documents' && (
          <DocumentsStep
            entries={documents}
            isMedicalLicenseRequired={
              !['semi_mandatory', 'optional', 'not_required'].includes(
                councilInfo?.registration_required ?? ''
              )
            }
            onUpload={handleUploadDocument}
            onDelete={handleDeleteDocument}
            onNext={() => goToStepIndex(5)}
            onBack={goBack}
          />
        )}
        {currentStep === 'practices' && (
          <PracticesStep
            doctorId={doctorId}
            entries={practices}
            practiceTypes={practiceTypes}
            onAdd={handleAddPractice}
            onDelete={handleDeletePractice}
            onAddTiming={handleAddPracticeTiming}
            onNext={() => goToStepIndex(6)}
            onBack={goBack}
          />
        )}
        {currentStep === 'services' && (
          <ServicesStep
            doctorId={doctorId}
            entries={services}
            categories={serviceCategories}
            medicalServices={medicalServices}
            onAdd={handleAddService}
            onDelete={handleDeleteService}
            onNext={() => goToStepIndex(7)}
            onBack={goBack}
          />
        )}
        {currentStep === 'bankDetails' && (
          <BankDetailsStep
            doctorId={doctorId}
            bankDetails={bankDetails}
            lastSavedAccountNumber={lastSavedAccountNumber}
            onSave={handleSaveBankDetails}
            onNext={() => goToStepIndex(8)}
            onBack={goBack}
          />
        )}
        {currentStep === 'bio' && (
          <BioStep
            profile={profile}
            onSave={handleSaveBio}
            onNext={() => goToStepIndex(9)}
            onBack={goBack}
          />
        )}
        {currentStep === 'review' && (
          <ReviewSubmitStep
            profile={profile}
            completionPercentage={completionPercentage}
            missingRequirements={missingRequirements}
            canSubmit={canSubmit}
            onboardingStatus={onboardingStatus}
            verificationNotes={verificationNotes}
            nextAction={nextAction}
            onSubmit={handleSubmitForVerification}
            onBack={goBack}
            submitting={saving}
          />
        )}
      </div>
    </div>
  );
}
